import React from 'react';
import { WasteMetric } from '@/types';

interface WasteScorecardChartProps {
  data: WasteMetric[];
}

const statusConfig: Record<string, { color: string; bar: string; label: string }> = {
  good:    { color: 'text-emerald-400', bar: 'bg-emerald-400', label: 'On Target' },
  warning: { color: 'text-amber-400',   bar: 'bg-amber-400',   label: 'Above Target' },
  poor:    { color: 'text-red-400',      bar: 'bg-red-400',     label: 'Needs Attention' },
};

const WasteScorecardChart: React.FC<WasteScorecardChartProps> = ({ data }) => {
  const getProgressPct = (current: number, target: number) => {
    if (current <= target) return 100;
    return Math.max(0, (target / current) * 100);
  };

  return (
    <div className="bg-navy-card border border-navy-border rounded-xl p-6">
      <h3 className="text-base font-semibold text-text-heading mb-5">Waste Scorecard</h3>
      
      <div className="space-y-5">
        {data.map((metric, index) => {
          const pct = getProgressPct(metric.current, metric.target);
          const cfg = statusConfig[metric.status] || statusConfig.good;
          
          return (
            <div key={index}>
              <div className="flex justify-between items-baseline mb-1.5">
                <span className="text-xs font-medium text-text-primary">{metric.category}</span>
                <div className="flex items-center space-x-3 text-[11px]">
                  <span className="text-text-muted">Target: {metric.target}</span>
                  <span className={`font-semibold ${cfg.color}`}>{metric.current}</span>
                </div>
              </div>
              
              <div className="h-1.5 bg-navy-border/40 rounded-full overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all duration-700 ${cfg.bar}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              
              <div className="flex items-center space-x-1.5 mt-1">
                <div className={`w-1.5 h-1.5 rounded-full ${cfg.bar}`} />
                <span className="text-[10px] text-text-muted">{cfg.label}</span>
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Summary */}
      <div className="mt-6 pt-4 border-t border-navy-border/50 grid grid-cols-3 gap-4 text-center">
        {[
          { label: 'On Target', count: data.filter(m => m.status === 'good').length, color: 'text-emerald-400' },
          { label: 'At Risk', count: data.filter(m => m.status === 'warning').length, color: 'text-amber-400' },
          { label: 'Critical', count: data.filter(m => m.status === 'poor').length, color: 'text-red-400' },
        ].map((s, i) => (
          <div key={i}>
            <p className={`text-lg font-bold ${s.color}`}>{s.count}</p>
            <p className="text-[10px] text-text-muted">{s.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default WasteScorecardChart;
